import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, LogOut, Search, User, Bell, ChevronDown, Crown } from 'lucide-react';
import axiosInstance from '../../utils/axiosInstance';

const DashboardTopBar = ({ onOpenMobileNav, userData, matchResult }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);

  const userDisplayName = userData?.first_name && userData?.last_name
    ? `${userData.first_name} ${userData.last_name}`
    : userData?.email || 'Account';

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const r = await axiosInstance.get("/subscription/status");
        setSubscription(r.data);
      } catch {
        setSubscription(null);
      }
    };
    fetchSubscription();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('applyease_wizard_completed');
    try {
      window.postMessage({ source: 'applyease', action: 'RemoveToken' }, '*');
    } catch {}
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'AI Dashboard';
    if (path === '/job-tracker') return 'Job Tracker';
    if (path === '/job-agent') return 'AI Job Agent';
    if (path === '/pricing') return 'Subscription';
    if (path === '/settings') return 'Settings';
    if (path === '/profile') return 'My Profile';
    if (path === '/email') return 'Email Outreach';
    if (path === '/find-hr') return 'Lead Finder';
    return 'Dashboard';
  };

  return (
    <header className="h-14 md:h-16 lg:h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-4 md:px-6 lg:px-10 z-[30] sticky top-0 shrink-0">
      <div className="flex items-center gap-3 md:gap-6">
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="p-2 -ml-1 md:p-2.5 md:-ml-2 text-slate-500 hover:bg-slate-100/80 rounded-xl lg:hidden transition-all active:scale-90"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 md:w-6 md:h-6" />
        </button>
        
        <div className="flex flex-col">
            <h1 className="text-base md:text-lg lg:text-xl font-black text-slate-900 tracking-tight leading-none italic uppercase">{getPageTitle()}</h1>
            <div className="flex items-center gap-1 md:gap-1.5 mt-0.5 md:mt-1">
               <div className="w-1 h-1 md:w-1.5 md:h-1.5 rounded-full bg-emerald-500 shadow-sm shadow-emerald-200 animate-pulse" />
               <span className="text-[8px] md:text-[9px] lg:text-[10px] font-bold text-slate-400 uppercase tracking-widest">System Operational</span>
            </div>
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-4 lg:gap-8">
        {/* Plan Badge */}
        {subscription && (
          <div 
            onClick={() => navigate('/pricing')}
            className="hidden lg:flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl cursor-pointer hover:shadow-md transition-all group"
          >
            <Crown size={16} className="text-blue-600" />
            <div className="flex flex-col">
              <span className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{subscription.tier?.name || 'Starter'} Plan</span>
              {subscription.trial?.active && (
                <span className="text-[8px] font-bold text-emerald-500">{subscription.trial.daysLeft} days trial left</span>
              )}
            </div>
          </div>
        )}

        {/* Match HUD - Modernized */}
        {matchResult && matchResult.percent > 0 && (
          <div className="hidden xl:flex items-center gap-2.5 pl-2 pr-4 py-1.5 bg-blue-50 border border-blue-100 rounded-2xl group transition-all hover:bg-white hover:shadow-lg hover:shadow-blue-50">
             <div className="w-8 h-8 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200 scale-90">
                <Search size={14} className="group-hover:rotate-12 transition-transform" />
             </div>
             <div className="flex flex-col">
                <span className="text-[10px] font-black text-blue-400 uppercase tracking-tighter leading-none">Job Match</span>
                <span className="text-xs font-black text-blue-700 leading-none mt-1">{matchResult.percent}% Score</span>
             </div>
          </div>
        )}
        
        <div className="hidden md:flex items-center gap-3">
           <button className="p-2.5 text-slate-400 hover:text-slate-900 transition-colors relative">
              <Bell size={20} />
              <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 border-2 border-white rounded-full" />
           </button>
        </div>

        <div className="h-6 md:h-8 w-px bg-slate-200/60 hidden md:block" />

        <div className="flex items-center gap-2 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3 pl-1 pr-1 md:pr-1.5 py-1 bg-slate-50 border border-slate-100 rounded-xl md:rounded-2xl group cursor-pointer hover:bg-white hover:shadow-md transition-all">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl md:rounded-[14px] flex items-center justify-center font-black text-xs md:text-sm border-2 border-white shadow-lg shadow-blue-100 group-hover:scale-105 transition-transform" onClick={() => navigate('/profile')}>
              {String(userDisplayName || 'U').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex flex-col text-left pr-2 hidden sm:flex" onClick={() => navigate('/profile')}>
              <div className="text-xs font-black text-slate-900 tracking-tight leading-none uppercase italic">{userDisplayName.split(' ')[0]}</div>
              <div className="text-[10px] text-slate-400 font-bold mt-1 tracking-tighter truncate max-w-[100px]">{userData?.email || 'Premium User'}</div>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleLogout}
            className="p-2 md:p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all duration-300 active:scale-90"
            aria-label="Log out"
            title="Log out"
          >
            <LogOut size={18} className="md:w-5 md:h-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default DashboardTopBar;

